/**
 * API Client for CoachSight Backend
 *
 * Provides a typed interface to the backend API with:
 * - Centralized base URL and JWT authentication
 * - Query parameters and JSON body support
 * - Abort signal support for cancellation
 * - Normalized error handling
 * - Automatic 401 logout on unauthorized
 */

import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

export interface ApiErrorResponse {
  status: number
  message: string
  code?: string
  details?: Record<string, unknown>
  error?: {
    code?: string
    message?: string
    details?: Record<string, unknown>
  }
}

export interface ApiError extends Error {
  status?: number
  code?: string
  details?: Record<string, unknown>
  retryAfterSeconds?: number
}

type AuthBoundRequestConfig = InternalAxiosRequestConfig & { expectedAuthToken?: string }

export class ApiClient {
  private client: AxiosInstance

  constructor(
    baseURL: string = import.meta.env.VITE_CSAPI_URL || '',
    options: { preserveUnauthorizedCodes?: readonly string[]; logoutOnUnauthorized?: boolean } = {}
  ) {
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor: Add JWT token
    this.client.interceptors.request.use((config: AuthBoundRequestConfig) => {
      const token = localStorage.getItem('token')
      const expected = config.expectedAuthToken
      delete config.expectedAuthToken
      if (expected !== undefined) {
        // A queued score write must never borrow a different browser session's
        // bearer, including one changed in another tab after its tap.
        if (!expected || token !== expected) throw new Error('Assessment session changed before request dispatch')
        config.headers.Authorization = `Bearer ${expected}`
        return config
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor: Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const code = (error.response?.data as ApiErrorResponse | undefined)?.error?.code
        if (options.logoutOnUnauthorized !== false && error.response?.status === 401 && !options.preserveUnauthorizedCodes?.includes(code || '')) {
          // Clear auth and trigger logout
          localStorage.removeItem('token')
          const authStore = useAuthStore()
          authStore.logout()
        }
        return Promise.reject(this.normalizeError(error))
      }
    )
  }

  async get<T = unknown>(
    path: string,
    query?: Record<string, unknown>,
    signal?: AbortSignal,
    expectedAuthToken?: string
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(path, {
      params: query,
      signal,
      ...(expectedAuthToken !== undefined ? { expectedAuthToken } : {}),
    })
    return response.data
  }

  async post<T = unknown>(
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    signal?: AbortSignal,
    timeoutMs?: number,
    expectedAuthToken?: string
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(path, body, {
      params: query,
      signal,
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
      ...(expectedAuthToken !== undefined ? { expectedAuthToken } : {}),
    })
    return response.data
  }

  async put<T = unknown>(
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    signal?: AbortSignal,
    expectedAuthToken?: string
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(path, body, {
      params: query,
      signal,
      ...(expectedAuthToken !== undefined ? { expectedAuthToken } : {}),
    })
    return response.data
  }

  async patch<T = unknown>(
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(path, body, {
      params: query,
      signal,
    })
    return response.data
  }

  async delete<T = unknown>(
    path: string,
    query?: Record<string, unknown>,
    signal?: AbortSignal,
    body?: unknown
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(path, {
      data: body,
      params: query,
      signal,
    })
    return response.data
  }

  private normalizeError(error: AxiosError): ApiError {
    const apiError = new Error() as ApiError

    if (error.response) {
      const data = error.response.data as Partial<ApiErrorResponse>
      const responseError = data.error
      apiError.status = error.response.status
      const retryAfter = Number(error.response.headers?.['retry-after'])
      if (Number.isFinite(retryAfter) && retryAfter >= 0) apiError.retryAfterSeconds = retryAfter
      apiError.message = responseError?.message || data.message || error.message
      apiError.code = responseError?.code || data.code
      apiError.details = responseError?.details || data.details
    } else if (error.request) {
      apiError.message = 'Network error: No response from server'
      apiError.code = 'NETWORK_ERROR'
    } else {
      apiError.message = error.message || 'Unknown error'
    }

    return apiError
  }
}

export const apiClient = new ApiClient()
